import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import {
  FileInfo,
  ProcessedImage,
  BatchProcessResult,
  ProcessProgress,
  FileStatus,
  ErrorCode,
  ProcessError,
  EventHandlers
} from '../types';
import { ImageProcessor } from './ImageProcessor';
import { PerformanceUtils } from '../utils/performanceUtils';
import { FileUtils } from '../utils/fileUtils';

// 状态接口
interface BatchProcessorState {
  files: FileInfo[];
  processedImages: ProcessedImage[];
  isProcessing: boolean;
  isPaused: boolean;
  currentProgress: ProcessProgress | null;
  batchResult: BatchProcessResult | null;
  selectedFileIds: string[];
}

// 动作类型
type BatchProcessorAction =
  | { type: 'ADD_FILES'; payload: FileInfo[] }
  | { type: 'REMOVE_FILE'; payload: string }
  | { type: 'UPDATE_FILE'; payload: { id: string; updates: Partial<FileInfo> } }
  | { type: 'START_PROCESSING' }
  | { type: 'PAUSE_PROCESSING' }
  | { type: 'RESUME_PROCESSING' }
  | { type: 'STOP_PROCESSING' }
  | { type: 'UPDATE_PROGRESS'; payload: ProcessProgress }
  | { type: 'ADD_PROCESSED_IMAGE'; payload: ProcessedImage }
  | { type: 'SET_BATCH_RESULT'; payload: BatchProcessResult }
  | { type: 'SELECT_FILES'; payload: string[] }
  | { type: 'CLEAR_ALL' };

// 初始状态
const initialState: BatchProcessorState = {
  files: [],
  processedImages: [],
  isProcessing: false,
  isPaused: false,
  currentProgress: null,
  batchResult: null,
  selectedFileIds: []
};

// Reducer
const batchProcessorReducer = (
  state: BatchProcessorState,
  action: BatchProcessorAction
): BatchProcessorState => {
  switch (action.type) {
    case 'ADD_FILES':
      return {
        ...state,
        files: [...state.files, ...action.payload]
      };

    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter(f => f.id !== action.payload),
        processedImages: state.processedImages.filter(p => p.id !== action.payload),
        selectedFileIds: state.selectedFileIds.filter(id => id !== action.payload)
      };

    case 'UPDATE_FILE':
      return {
        ...state,
        files: state.files.map(f =>
          f.id === action.payload.id ? { ...f, ...action.payload.updates } : f
        )
      };

    case 'START_PROCESSING':
      return {
        ...state,
        isProcessing: true,
        isPaused: false,
        batchResult: null
      };

    case 'PAUSE_PROCESSING':
      return {
        ...state,
        isPaused: true
      };

    case 'RESUME_PROCESSING':
      return {
        ...state,
        isPaused: false
      };

    case 'STOP_PROCESSING':
      return {
        ...state,
        isProcessing: false,
        isPaused: false,
        currentProgress: null
      };

    case 'UPDATE_PROGRESS':
      return {
        ...state,
        currentProgress: action.payload
      };

    case 'ADD_PROCESSED_IMAGE':
      return {
        ...state,
        processedImages: [...state.processedImages, action.payload]
      };

    case 'SET_BATCH_RESULT':
      return {
        ...state,
        batchResult: action.payload,
        isProcessing: false,
        currentProgress: null
      };

    case 'SELECT_FILES':
      return {
        ...state,
        selectedFileIds: action.payload
      };

    case 'CLEAR_ALL':
      // 清理所有资源
      state.files.forEach(FileUtils.cleanupFileInfo);
      state.processedImages.forEach(img => URL.revokeObjectURL(img.processedUrl));

      return {
        ...initialState
      };

    default:
      return state;
  }
};

// Context类型
interface BatchProcessorContextType extends BatchProcessorState {
  addFiles: (files: FileInfo[]) => void;
  removeFile: (id: string) => void;
  updateFile: (id: string, updates: Partial<FileInfo>) => void;
  startProcessing: (fileIds?: string[]) => Promise<void>;
  pauseProcessing: () => void;
  resumeProcessing: () => void;
  stopProcessing: () => void;
  selectFiles: (fileIds: string[]) => void;
  clearAll: () => void;
  downloadSingle: (imageId: string) => void;
  downloadBatch: (imageIds?: string[]) => Promise<void>;
  setEventHandlers: (handlers: Partial<EventHandlers>) => void;
}

// Context
const BatchProcessorContext = createContext<BatchProcessorContextType | null>(null);

// Provider组件
export const BatchProcessorProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [state, dispatch] = useReducer(batchProcessorReducer, initialState);
  const imageProcessor = useRef(new ImageProcessor());
  const eventHandlers = useRef<Partial<EventHandlers>>({});
  const abortController = useRef<AbortController | null>(null);

  // 添加文件
  const addFiles = useCallback((files: FileInfo[]) => {
    dispatch({ type: 'ADD_FILES', payload: files });
    files.forEach(file => {
      eventHandlers.current.onFileAdded?.(file);
    });
  }, []);

  // 删除文件
  const removeFile = useCallback((id: string) => {
    const file = state.files.find(f => f.id === id);
    if (file) {
      FileUtils.cleanupFileInfo(file);
      const processedImage = state.processedImages.find(p => p.id === id);
      if (processedImage) {
        URL.revokeObjectURL(processedImage.processedUrl);
      }
    }
    dispatch({ type: 'REMOVE_FILE', payload: id });
    eventHandlers.current.onFileRemoved?.(id);
  }, [state.files, state.processedImages]);

  // 更新文件
  const updateFile = useCallback((id: string, updates: Partial<FileInfo>) => {
    dispatch({ type: 'UPDATE_FILE', payload: { id, updates } });
  }, []);

  // 开始处理
  const startProcessing = useCallback(async (fileIds?: string[]) => {
    const filesToProcess = fileIds
      ? state.files.filter(f => fileIds.includes(f.id))
      : state.files.filter(f => f.status === FileStatus.PENDING);

    if (filesToProcess.length === 0) {
      return;
    }

    dispatch({ type: 'START_PROCESSING' });
    abortController.current = new AbortController();

    const startTime = Date.now();
    const result: BatchProcessResult = {
      totalFiles: filesToProcess.length,
      successCount: 0,
      errorCount: 0,
      cancelledCount: 0,
      processedImages: [],
      errors: [],
      startTime
    };

    eventHandlers.current.onProcessingStart?.(filesToProcess.map(f => f.id));

    // 获取推荐的批次大小
    const batchSize = PerformanceUtils.getRecommendedBatchSize();

    try {
      // 分批处理
      for (let i = 0; i < filesToProcess.length; i += batchSize) {
        if (abortController.current.signal.aborted) {
          break;
        }

        const batch = filesToProcess.slice(i, i + batchSize);

        // 创建进度计算函数
        const createProgressInfo = (fileInfo: FileInfo, fileIndex: number, step: string, fileProgress: number) => {
          const elapsed = Date.now() - startTime;
          const completedFiles = fileIndex; // 使用文件索引而不是processedCount

          return {
            fileId: fileInfo.id,
            fileName: fileInfo.name,
            currentStep: step,
            progress: fileProgress,
            overallProgress: (fileIndex / filesToProcess.length) * 100,
            currentFileIndex: fileIndex,
            totalFiles: filesToProcess.length,
            estimatedTimeRemaining: completedFiles > 0
              ? (elapsed / completedFiles) * (filesToProcess.length - completedFiles) / 1000
              : undefined,
            averageProcessingTime: completedFiles > 0
              ? elapsed / completedFiles / 1000
              : undefined
          } as ProcessProgress;
        };

        // 处理当前批次
        const batchPromises = batch.map(async (fileInfo, batchIndex) => {
          const fileIndex = i + batchIndex;

          try {
            // 更新文件状态
            updateFile(fileInfo.id, {
              status: FileStatus.PROCESSING,
              progress: 0
            });

            // 更新进度
            const progress = createProgressInfo(fileInfo, fileIndex, '准备处理...', 0);

            dispatch({ type: 'UPDATE_PROGRESS', payload: progress });
            eventHandlers.current.onProcessingProgress?.(progress);

            // 处理图片
            const processResult = await imageProcessor.current.processImage(
              fileInfo,
              (fileProgress) => {
                updateFile(fileInfo.id, { progress: fileProgress });

                const stepText = fileProgress < 30 ? '加载图片...' :
                               fileProgress < 70 ? '处理图片...' :
                               fileProgress < 90 ? '生成结果...' : '完成处理';

                const updatedProgress = createProgressInfo(fileInfo, fileIndex, stepText, fileProgress);

                dispatch({ type: 'UPDATE_PROGRESS', payload: updatedProgress });
                eventHandlers.current.onProcessingProgress?.(updatedProgress);
              }
            );

            if (processResult.success && processResult.data) {
              // 处理成功
              updateFile(fileInfo.id, {
                status: FileStatus.COMPLETED,
                progress: 100,
                processedData: processResult.data
              });

              dispatch({ type: 'ADD_PROCESSED_IMAGE', payload: processResult.data });
              result.processedImages.push(processResult.data);
              result.successCount++;
            } else {
              // 处理失败
              const error = processResult.error || {
                code: ErrorCode.UNKNOWN_ERROR,
                message: '处理失败',
                recoverable: false,
                timestamp: Date.now()
              };

              updateFile(fileInfo.id, {
                status: FileStatus.ERROR,
                error
              });

              result.errors.push({ fileId: fileInfo.id, error });
              result.errorCount++;

              eventHandlers.current.onProcessingError?.(error, fileInfo.id);
            }

          } catch (error) {
            // 异常处理
            const processError: ProcessError = {
              code: ErrorCode.UNKNOWN_ERROR,
              message: error instanceof Error ? error.message : '未知错误',
              recoverable: false,
              timestamp: Date.now()
            };

            updateFile(fileInfo.id, {
              status: FileStatus.ERROR,
              error: processError
            });

            result.errors.push({ fileId: fileInfo.id, error: processError });
            result.errorCount++;

            eventHandlers.current.onProcessingError?.(processError, fileInfo.id);
          }
        });

        await Promise.allSettled(batchPromises);

        // 批次间延迟
        if (i + batchSize < filesToProcess.length && !abortController.current.signal.aborted) {
          await PerformanceUtils.delay(50);
        }
      }

    } catch (error) {
      console.error('批量处理出错:', error);
    }

    // 完成处理
    result.endTime = Date.now();
    result.totalProcessingTime = result.endTime - result.startTime;

    // 计算取消的文件数量
    result.cancelledCount = filesToProcess.length - result.successCount - result.errorCount;

    dispatch({ type: 'SET_BATCH_RESULT', payload: result });
    dispatch({ type: 'STOP_PROCESSING' });

    eventHandlers.current.onProcessingComplete?.(result);

    // 更新性能统计
    PerformanceUtils.updatePerformanceStats({
      totalProcessedFiles: result.successCount,
      totalProcessingTime: result.totalProcessingTime || 0,
      errorRate: result.errorCount / result.totalFiles,
      cancelRate: result.cancelledCount / result.totalFiles
    });

  }, [state.files, updateFile]);

  // 暂停处理
  const pauseProcessing = useCallback(() => {
    dispatch({ type: 'PAUSE_PROCESSING' });
  }, []);

  // 恢复处理
  const resumeProcessing = useCallback(() => {
    dispatch({ type: 'RESUME_PROCESSING' });
  }, []);

  // 停止处理
  const stopProcessing = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
    }
    dispatch({ type: 'STOP_PROCESSING' });
  }, []);

  // 选择文件
  const selectFiles = useCallback((fileIds: string[]) => {
    dispatch({ type: 'SELECT_FILES', payload: fileIds });
  }, []);

  // 清理所有
  const clearAll = useCallback(() => {
    if (state.isProcessing) {
      stopProcessing();
    }
    dispatch({ type: 'CLEAR_ALL' });
  }, [state.isProcessing, stopProcessing]);

  // 下载单个文件
  const downloadSingle = useCallback((imageId: string) => {
    const processedImage = state.processedImages.find(p => p.id === imageId);
    if (processedImage) {
      FileUtils.downloadFile(processedImage.processedBlob, processedImage.downloadName);
      eventHandlers.current.onDownloadStart?.([imageId]);
      eventHandlers.current.onDownloadComplete?.(1);
    }
  }, [state.processedImages]);

  // 批量下载
  const downloadBatch = useCallback(async (imageIds?: string[]) => {
    const imagesToDownload = imageIds
      ? state.processedImages.filter(p => imageIds.includes(p.id))
      : state.processedImages;

    if (imagesToDownload.length === 0) {
      return;
    }

    eventHandlers.current.onDownloadStart?.(imagesToDownload.map(p => p.id));

    if (imagesToDownload.length === 1) {
      // 单个文件直接下载
      const image = imagesToDownload[0];
      FileUtils.downloadFile(image.processedBlob, image.downloadName);
    } else {
      // 多个文件打包下载
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      imagesToDownload.forEach(image => {
        zip.file(image.downloadName, image.processedBlob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipName = `processed_images_${Date.now()}.zip`;
      FileUtils.downloadFile(zipBlob, zipName);
    }

    eventHandlers.current.onDownloadComplete?.(imagesToDownload.length);
  }, [state.processedImages]);

  // 设置事件处理器
  const setEventHandlers = useCallback((handlers: Partial<EventHandlers>) => {
    eventHandlers.current = { ...eventHandlers.current, ...handlers };
  }, []);

  const contextValue: BatchProcessorContextType = {
    ...state,
    addFiles,
    removeFile,
    updateFile,
    startProcessing,
    pauseProcessing,
    resumeProcessing,
    stopProcessing,
    selectFiles,
    clearAll,
    downloadSingle,
    downloadBatch,
    setEventHandlers
  };

  return (
    <BatchProcessorContext.Provider value={contextValue}>
      {children}
    </BatchProcessorContext.Provider>
  );
};

// Hook
export const useBatchProcessor = () => {
  const context = useContext(BatchProcessorContext);
  if (!context) {
    throw new Error('useBatchProcessor must be used within BatchProcessorProvider');
  }
  return context;
};